import React, { useEffect, useRef } from 'react';
import { MapPin, Phone, Mail, Clock, MessageCircle, ExternalLink } from 'lucide-react';

const WA_NUMBER = '529514997959';
const WA_LINK = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent('Hola, me interesa obtener una cotización de Grupo Constructor Yescas.')}`;
const MAPS_LINK = 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent('Geranios 302, Las Flores Sur, Santa Lucía del Camino, Oaxaca');

const useScrollAnimation = (threshold = 0.1) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.querySelectorAll('.fade-up, .fade-left, .fade-right').forEach((child) => {
            child.classList.add('visible');
          });
          observer.unobserve(el);
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return ref;
};

const Contact = () => {
  const ref = useScrollAnimation();

  const infoItems = [
    { 
      icon: MapPin, 
      label: 'Dirección', 
      value: 'Geranios No. 302, Col. Las Flores Sur, Santa Lucía del Camino, Oaxaca. C.P. 71245',
      link: MAPS_LINK,
      linkLabel: 'Ver en Google Maps'
    },
    { icon: Phone, label: 'Teléfono', value: '951 502 62 20 · 951 499 79 59' },
    { icon: Mail, label: 'Correo', value: 'grupoconstructoryescas@hotmail.com' },
    { icon: Clock, label: 'Horario', value: 'Lun–Vie: 9am–6pm · Sáb: 9am–3pm' },
  ];

  return (
    <section id="contacto" ref={ref} style={{ padding: '8rem 2rem', background: 'var(--bg-secondary)', position: 'relative', overflow: 'hidden' }}>
      {/* Top accent */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'var(--red)' }} />
      
      {/* Left accent */}
      <div style={{ position: 'absolute', left: 0, top: '15%', bottom: '15%', width: 4, background: 'var(--red)', borderRadius: '0 2px 2px 0' }} />

      <div style={{ position: 'relative', zIndex: 1, maxWidth: 900, margin: '0 auto' }}>

        {/* Header */}
        <div className="fade-up" style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <div className="section-label">Háblanos</div>
          <h2 style={{ fontFamily: 'var(--font-heading)', fontWeight: 900, fontSize: 'clamp(2rem, 4vw, 3rem)', color: 'var(--text-primary)', marginTop: '0.5rem' }}>
            Inicia tu <span style={{ color: 'var(--red)' }}>Proyecto</span>
          </h2>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: '1rem', color: 'var(--gray)', marginTop: '1rem', maxWidth: 500, margin: '1rem auto 0', lineHeight: 1.7 }}>
            Contáctanos y nuestro equipo se pondrá en contacto contigo.
          </p>
        </div>

        {/* Contact Info Card */}
        <div className="fade-up" style={{ 
          background: 'var(--bg-card)',
          border: '1px solid var(--border-color)', 
          borderRadius: 20, 
          padding: '3rem', 
          boxShadow: '0 8px 40px rgba(0,0,0,0.06)',
          marginBottom: '2rem'
        }}>
          <h3 style={{ 
            fontFamily: 'var(--font-heading)', 
            fontWeight: 700, 
            fontSize: '1.25rem', 
            color: 'var(--text-primary)', 
            marginBottom: '2rem',
            textAlign: 'center'
          }}>
            Información de Contacto
          </h3>
          
          {/* Info Grid */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(2, 1fr)', 
            gap: '2rem',
            marginBottom: '2.5rem'
          }} className="contact-info-grid">
            {infoItems.map((item, i) => (
              <div key={i} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                <div style={{ 
                  width: 48, 
                  height: 48, 
                  background: 'var(--red)', 
                  borderRadius: 12, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  flexShrink: 0 
                }}>
                  <item.icon size={22} color="white" strokeWidth={1.5} />
                </div>
                <div>
                  <div style={{ 
                    fontFamily: 'var(--font-heading)', 
                    fontWeight: 600, 
                    fontSize: '0.7rem', 
                    letterSpacing: '0.15em', 
                    textTransform: 'uppercase', 
                    color: 'var(--gray)', 
                    marginBottom: '0.3rem' 
                  }}>{item.label}</div>
                  <div style={{ 
                    fontFamily: 'var(--font-body)', 
                    fontSize: '0.95rem', 
                    color: 'var(--text-primary)',
                    lineHeight: 1.5
                  }}>{item.value}</div>
                  {item.link && (
                    <a 
                      href={item.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.3rem',
                        fontFamily: 'var(--font-heading)',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: 'var(--red)',
                        textDecoration: 'none',
                        marginTop: '0.5rem',
                        transition: 'gap 0.2s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.gap = '0.5rem'}
                      onMouseLeave={(e) => e.currentTarget.style.gap = '0.3rem'}
                    >
                      {item.linkLabel} <ExternalLink size={12} />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: '1.25rem' 
          }} className="contact-cta-grid">
            {/* WhatsApp CTA */}
            <a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex', alignItems: 'center', gap: '1rem',
                background: '#25D366', borderRadius: 14, padding: '1.25rem 1.75rem',
                textDecoration: 'none',
                transition: 'background 0.3s ease, transform 0.2s ease',
                boxShadow: '0 4px 20px rgba(37,211,102,0.25)',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#1ebe5d'; e.currentTarget.style.transform = 'translateY(-3px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#25D366'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              <div style={{ 
                width: 48, 
                height: 48, 
                background: 'rgba(255,255,255,0.2)', 
                borderRadius: '50%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                flexShrink: 0 
              }}>
                <MessageCircle size={24} color="white" fill="white" strokeWidth={0} />
              </div>
              <div>
                <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 800, fontSize: '1rem', color: 'white', lineHeight: 1.2 }}>Escríbenos por WhatsApp</div>
                <div style={{ fontFamily: 'var(--font-body)', fontSize: '0.8rem', color: 'rgba(255,255,255,0.9)', marginTop: 4 }}>Respuesta inmediata</div>
              </div>
            </a>

            {/* Call CTA */}
            <a
              href="tel:+529514997959"
              style={{
                display: 'flex', alignItems: 'center', gap: '1rem',
                background: 'var(--red)', borderRadius: 14, padding: '1.25rem 1.75rem',
                textDecoration: 'none',
                transition: 'background 0.3s ease, transform 0.2s ease',
                boxShadow: '0 4px 20px rgba(196,30,58,0.25)',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--red-dark)'; e.currentTarget.style.transform = 'translateY(-3px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--red)'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              <div style={{ 
                width: 48, 
                height: 48, 
                background: 'rgba(255,255,255,0.15)', 
                borderRadius: '50%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                flexShrink: 0 
              }}>
                <Phone size={24} color="white" strokeWidth={1.5} />
              </div>
              <div>
                <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 800, fontSize: '1rem', color: 'white', lineHeight: 1.2 }}>Llamar ahora</div>
                <div style={{ fontFamily: 'var(--font-body)', fontSize: '0.8rem', color: 'rgba(255,255,255,0.9)', marginTop: 4 }}>+52 951 499 7959</div>
              </div>
            </a>
          </div>
        </div>

        {/* Google Maps Embed */}
        <div className="fade-up" style={{
          borderRadius: 16,
          overflow: 'hidden',
          border: '1px solid var(--border-color)',
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
        }}>
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3814.0547889744!2d-96.68055!3d17.0645!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x85c72249e94f8f57%3A0x5c8d51e68a3d8f6e!2sGeranios%20302%2C%20Las%20Flores%20Sur%2C%2071245%20Santa%20Luc%C3%ADa%20del%20Camino%2C%20Oax.!5e0!3m2!1ses!2smx!4v1704067200000!5m2!1ses!2smx"
            width="100%"
            height="300"
            style={{ border: 0, display: 'block' }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Ubicación Grupo Constructor Yescas"
          />
        </div>
      </div>

      <style>{`
        @media (max-width: 700px) { 
          .contact-info-grid { grid-template-columns: 1fr !important; gap: 1.5rem !important; }
          .contact-cta-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
};

export default Contact;
